import { alpha, Box, Grid, Typography, useTheme } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useUpdateContactItem } from 'api/aws/contacts/useUpdateContactItem'
import { useUserItems } from 'api/aws/users/useUserItems'
import { useEntitiesFromSearch } from 'api/search'
import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { ProfileCardAdapter } from 'theme/components'
import { Contact } from 'types'
import { useContact } from '../../api/aws/useContact'
import { useSnackbarContext } from '../../context/SnackbarProvider/SnackbarProvider'
import DetailsLoading from '../Loading/DetailsLoading'
import CancelButton from '../ui/cancel-button'
import EditIconButton from '../ui/edit-icon-buttons'
import { SaveChangesButton } from '../ui/save-changes-button'
import { contactFieldConfigSections } from './contactFieldConfig'
import ContactReadOnlyField from './ContactReadOnlyField'
import { gridStyles } from './style'

type ChangedFieldMap = Record<string, { newValue: any; isDirty: boolean }>

const prettifyKey = (key: string) => {
  const parts = key.match(/[A-Z]?[a-z]+|[A-Z]+(?![a-z])/g)
  return parts ? parts.map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : key
}

const ContactProfile: React.FC = () => {
  const { contactId } = useParams()
  const queryClient = useQueryClient()
  const theme = useTheme()
  const { showSnackbar } = useSnackbarContext()

  // Data fetching
  const { data: contact, isLoading: contactLoading } = useContact(contactId)
  const { organizations } = useEntitiesFromSearch()
  const { data: users, isLoading: usersLoading } = useUserItems()
  // Edit state
  const [isEditing, setIsEditing] = useState(false)
  const [changedFields, setChangedFields] = useState<ChangedFieldMap>({})
  const [savingFields, setSavingFields] = useState<string[]>([])

  // Mutation
  const { mutate: updateItem } = useUpdateContactItem()

  // Normalize organizations for autocomplete
  const normalizedOrganizations = useMemo(
    () =>
      (organizations ?? [])
        .map(o => ({ id: o.id ?? o.org_uuid ?? o.PK, name: o.name ?? '', org_uuid: o.org_uuid, ...o }))
        .filter(o => !!o.id),
    [organizations]
  )

  const getFieldValue = (fieldName: string): any => {
    const changed = changedFields[fieldName]?.newValue
    if (changed !== undefined) return changed

    const originalValue = contact?.[fieldName as keyof Contact]

    // Handle name fields that might have alternate keys
    if (fieldName === 'firstName') return contact?.firstName ?? contact?.first_name ?? ''
    if (fieldName === 'lastName') return contact?.lastName ?? contact?.last_name ?? ''

    return originalValue ?? ''
  }

  const handleChange = (fieldName: string, newValue: any) => {
    if (typeof fieldName !== 'string') return

    const originalValue = contact?.[fieldName as keyof Contact]

    // Normalize array values
    const normalizedNewValue = Array.isArray(newValue) ? newValue.filter(v => v != null && v !== '') : newValue ?? ''

    const sameArray = (a: any[], b: any[]) =>
      Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every(v => b.includes(v))

    const isDirty =
      Array.isArray(normalizedNewValue) && Array.isArray(originalValue)
        ? !sameArray(normalizedNewValue, originalValue as any[])
        : normalizedNewValue !== originalValue

    setChangedFields(prev => ({
      ...prev,
      [fieldName]: { newValue: normalizedNewValue, isDirty },
    }))

    // Auto-update full name when name fields change
    if (['firstName', 'middleName', 'lastName'].includes(fieldName)) {
      const firstName = fieldName === 'firstName' ? newValue : getFieldValue('firstName')
      const middleName = fieldName === 'middleName' ? newValue : getFieldValue('middleName')
      const lastName = fieldName === 'lastName' ? newValue : getFieldValue('lastName')
      const fullName = `${firstName} ${middleName} ${lastName}`.replace(/\s+/g, ' ').trim()

      if (fullName !== contact?.name) {
        setChangedFields(prev => ({
          ...prev,
          name: { newValue: fullName, isDirty: true },
        }))
      }
    }
  }

  const handleUpdate = async () => {
    if (!contactId) return

    const dirtyEntries = Object.entries(changedFields).filter(([_, meta]) => meta?.isDirty)

    if (dirtyEntries.length === 0) {
      console.warn('No updates found')
      return
    }

    setSavingFields(dirtyEntries.map(([fieldName]) => fieldName))

    // Update each dirty field
    for (const [fieldName, meta] of dirtyEntries) {
      updateItem(
        {
          primaryKey: 'contacts_uuid',
          tableName: 'contacts',
          itemId: contactId,
          fieldName,
          newValue: meta.newValue,
        },
        {
          onSuccess: () => {
            showSnackbar(`Contact ${prettifyKey(fieldName)} updated successfully`, 'success')
            setSavingFields(prev => prev.filter(f => f !== fieldName))
          },
          onError: err => {
            console.error(`Update failed for ${fieldName}`, err)
            setSavingFields(prev => prev.filter(f => f !== fieldName))
          },
        }
      )
    }

    // Clear state after updates
    setChangedFields({})
    setIsEditing(false)
    queryClient.invalidateQueries({ queryKey: ['contact', contactId] })
  }

  const handleCancel = () => {
    setChangedFields({})
    setSavingFields([])
    setIsEditing(false)
  }

  // Build field sections
  const contactFieldsSections = useMemo(() => {
    if (contactLoading || usersLoading) return []
    return contactFieldConfigSections(contact, normalizedOrganizations, handleChange)
  }, [contact, normalizedOrganizations, contactLoading, usersLoading])

  const hasDirtyFields = Object.values(changedFields).some(v => v?.isDirty)

  if (contactLoading) return <DetailsLoading />

  return (
    <Box
      display="flex"
      flexDirection="column"
      sx={{
        ...gridStyles(theme),
        position: 'relative',
        height: '100%',
        minHeight: '100%',
        transition: 'height 0.3s ease',
      }}
    >
      {/* Scrollable content area */}
      <Box
        flex={1}
        overflow="auto"
        sx={{
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            boxShadow: `inset 0 0 6px ${alpha(theme.palette.text.primary, 0.3)}`,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.background.scrollbar_thumb,
            borderRadius: '6px',
            '&:hover': {
              backgroundColor: 'text.secondary',
            },
          },
        }}
      >
        <ProfileCardAdapter
          sections={contactFieldsSections}
          isEditing={isEditing}
          entity={contact}
          getFieldValue={getFieldValue}
          onChange={handleChange}
          changedFields={changedFields}
          savingFields={savingFields}
          ReadOnlyFieldComponent={ContactReadOnlyField}
          renderContext={{ users, organizations: normalizedOrganizations }}
        />
      </Box>

      {/* Sticky footer for edit/save controls */}
      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: theme.palette.background.paper,
          px: 3,
          py: 1.5,
          boxShadow: `0 -2px 10px ${alpha(theme.palette.text.primary, 0.3)}`,
          zIndex: 10,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Grid container justifyContent="space-between" alignItems="center" spacing={1}>
          {isEditing ? (
            <>
              <Grid item>
                <Typography color={theme.palette.text.primary} variant="subtitle1">
                  Changed Fields:
                </Typography>
              </Grid>

              <Grid item sx={{ maxWidth: '60%', flexGrow: 1, overflow: 'hidden' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  {Object.entries(changedFields)
                    .filter(([_, meta]) => meta.isDirty)
                    .map(([key]) => (
                      <Box
                        component="span"
                        key={key}
                        sx={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          border: `1px solid ${theme.palette.text.primary}`,
                          borderRadius: 1,
                          fontSize: '0.75rem',
                          color: theme.palette.text.secondary,
                          background: 'transparent',
                        }}
                      >
                        {prettifyKey(key)}
                      </Box>
                    ))}
                </Box>
              </Grid>
              <Grid item>
                <CancelButton onClick={handleCancel} disabled={false} />
              </Grid>
              <Grid item>
                <SaveChangesButton
                  onClick={handleUpdate}
                  disabled={!hasDirtyFields}
                  variant="contained"
                  sx={{ color: theme.palette.text.primary }}
                >
                  Save Changes
                </SaveChangesButton>
              </Grid>
            </>
          ) : (
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <EditIconButton onEdit={() => setIsEditing(true)} />
            </Grid>
          )}
        </Grid>
      </Box>
    </Box>
  )
}

export default ContactProfile
